import React, { useState } from "react";
import { usePnL } from "../hooks/usePnL";
import { PerformanceMetrics, TradePnL } from "../types/pnl";
import "./PnLDashboard.css";

interface PnLDashboardProps {
  className?: string;
  showRealTimeUpdates?: boolean;
  includeFees?: boolean;
}

const PnLDashboard: React.FC<PnLDashboardProps> = ({
  className = "",
  showRealTimeUpdates = true,
  includeFees = true,
}) => {
  const [activeTab, setActiveTab] = useState<
    "overview" | "trades" | "performance" | "charts"
  >("overview");
  const [showPnLUpdates, setShowPnLUpdates] = useState(showRealTimeUpdates);

  const {
    portfolioPnL,
    performanceMetrics,
    tradePnLs,
    loading,
    error,
    pnlUpdates,
    recalculatePnL,
    currentPrices,
    updateCurrentPrice,
    dateRange,
    setDateRange,
  } = usePnL({
    includeFees,
    autoRefresh: showRealTimeUpdates,
    refreshInterval: 30000,
  });

  if (loading) {
    return (
      <div className={`pnl-dashboard ${className}`}>
        <div className="loading-spinner">Loading P&L data...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={`pnl-dashboard ${className}`}>
        <div className="error-message">
          Error loading P&L data: {error.message}
          <button onClick={recalculatePnL} className="retry-button">
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className={`pnl-dashboard ${className}`}>
      {/* Header */}
      <div className="dashboard-header">
        <h2>P&L Dashboard</h2>
        <div className="header-controls">
          <button
            onClick={recalculatePnL}
            className="refresh-button"
            title="Refresh P&L calculations"
          >
            🔄 Refresh
          </button>
          <label className="real-time-toggle">
            <input
              type="checkbox"
              checked={showPnLUpdates}
              onChange={(e) => setShowPnLUpdates(e.target.checked)}
            />
            Real-time Updates
          </label>
        </div>
      </div>

      {/* Portfolio Summary Cards */}
      {portfolioPnL && (
        <div className="portfolio-summary">
          <div className="summary-card total-pnl">
            <h3>Total P&L</h3>
            <div
              className={`pnl-value ${portfolioPnL.totalPnL >= 0 ? "positive" : "negative"}`}
            >
              ${portfolioPnL.totalPnL.toFixed(2)}
            </div>
            <div className="pnl-percentage">
              {portfolioPnL.returnPercentage.toFixed(2)}%
            </div>
          </div>

          <div className="summary-card realized-pnl">
            <h3>Realized P&L</h3>
            <div
              className={`pnl-value ${portfolioPnL.realizedPnL >= 0 ? "positive" : "negative"}`}
            >
              ${portfolioPnL.realizedPnL.toFixed(2)}
            </div>
          </div>

          <div className="summary-card unrealized-pnl">
            <h3>Unrealized P&L</h3>
            <div
              className={`pnl-value ${portfolioPnL.unrealizedPnL >= 0 ? "positive" : "negative"}`}
            >
              ${portfolioPnL.unrealizedPnL.toFixed(2)}
            </div>
          </div>

          <div className="summary-card cost-basis">
            <h3>Cost Basis</h3>
            <div className="pnl-value">
              ${portfolioPnL.costBasis.toFixed(2)}
            </div>
          </div>

          <div className="summary-card market-value">
            <h3>Market Value</h3>
            <div className="pnl-value">
              ${portfolioPnL.marketValue.toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Navigation Tabs */}
      <div className="dashboard-tabs">
        <button
          className={`tab-button ${activeTab === "overview" ? "active" : ""}`}
          onClick={() => setActiveTab("overview")}
        >
          Overview
        </button>
        <button
          className={`tab-button ${activeTab === "trades" ? "active" : ""}`}
          onClick={() => setActiveTab("trades")}
        >
          Trades ({tradePnLs.length})
        </button>
        <button
          className={`tab-button ${activeTab === "performance" ? "active" : ""}`}
          onClick={() => setActiveTab("performance")}
        >
          Performance
        </button>
        <button
          className={`tab-button ${activeTab === "charts" ? "active" : ""}`}
          onClick={() => setActiveTab("charts")}
        >
          Charts
        </button>
      </div>

      {/* Tab Content */}
      <div className="tab-content">
        {activeTab === "overview" && (
          <OverviewTab
            portfolioPnL={portfolioPnL}
            performanceMetrics={performanceMetrics}
            tradePnLs={tradePnLs}
            currentPrices={currentPrices}
            updateCurrentPrice={updateCurrentPrice}
          />
        )}

        {activeTab === "trades" && (
          <TradesTab
            tradePnLs={tradePnLs}
            currentPrices={currentPrices}
            updateCurrentPrice={updateCurrentPrice}
          />
        )}

        {activeTab === "performance" && (
          <PerformanceTab
            performanceMetrics={performanceMetrics}
            dateRange={dateRange}
            setDateRange={setDateRange}
          />
        )}

        {activeTab === "charts" && (
          <ChartsTab
            performanceMetrics={performanceMetrics}
            tradePnLs={tradePnLs}
          />
        )}
      </div>

      {/* Real-time Updates Panel */}
      {showPnLUpdates && pnlUpdates.length > 0 && (
        <div className="pnl-updates-panel">
          <h3>Recent Updates ({pnlUpdates.length})</h3>
          <div className="updates-list">
            {pnlUpdates.slice(0, 5).map((update, index) => (
              <div key={index} className="update-item">
                <span className="update-type">{update.type}</span>
                <span className="update-trade">Trade #{update.tradeId}</span>
                <span className="update-change">
                  $
                  {(
                    update.newValue.totalPnL - update.oldValue.totalPnL
                  ).toFixed(2)}
                </span>
                <span className="update-time">
                  {new Date(update.timestamp).toLocaleTimeString()}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// Overview Tab Component
const OverviewTab: React.FC<{
  portfolioPnL: any;
  performanceMetrics: PerformanceMetrics | null;
  tradePnLs: TradePnL[];
  currentPrices: Record<string, number>;
  updateCurrentPrice: (symbol: string, price: number) => void;
}> = ({
  portfolioPnL,
  performanceMetrics,
  tradePnLs,
  currentPrices,
  updateCurrentPrice,
}) => {
  const [editingPrice, setEditingPrice] = useState<string | null>(null);
  const [newPrice, setNewPrice] = useState<string>("");

  const handlePriceUpdate = (symbol: string) => {
    const price = parseFloat(newPrice);
    if (!isNaN(price) && price > 0) {
      updateCurrentPrice(symbol, price);
      setEditingPrice(null);
      setNewPrice("");
    }
  };

  return (
    <div className="overview-tab">
      {/* Performance Metrics Grid */}
      {performanceMetrics && (
        <div className="performance-metrics-grid">
          <div className="metric-card">
            <h4>Win Rate</h4>
            <div className="metric-value">
              {performanceMetrics.winRate.toFixed(1)}%
            </div>
            <div className="metric-detail">
              {performanceMetrics.winningTrades} /{" "}
              {performanceMetrics.totalTrades} trades
            </div>
          </div>

          <div className="metric-card">
            <h4>Average Win</h4>
            <div className="metric-value positive">
              ${performanceMetrics.averageWin.toFixed(2)}
            </div>
          </div>

          <div className="metric-card">
            <h4>Average Loss</h4>
            <div className="metric-value negative">
              ${performanceMetrics.averageLoss.toFixed(2)}
            </div>
          </div>

          <div className="metric-card">
            <h4>Profit Factor</h4>
            <div className="metric-value">
              {performanceMetrics.profitFactor.toFixed(2)}
            </div>
          </div>

          <div className="metric-card">
            <h4>Max Drawdown</h4>
            <div className="metric-value negative">
              ${performanceMetrics.maxDrawdown.toFixed(2)}
            </div>
          </div>

          <div className="metric-card">
            <h4>Sharpe Ratio</h4>
            <div className="metric-value">
              {performanceMetrics.sharpeRatio.toFixed(2)}
            </div>
          </div>
        </div>
      )}

      {/* Current Prices Table */}
      <div className="current-prices-section">
        <h3>Current Market Prices</h3>
        <div className="prices-table">
          <table>
            <thead>
              <tr>
                <th>Symbol</th>
                <th>Current Price</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(currentPrices).map(([symbol, price]) => (
                <tr key={symbol}>
                  <td>{symbol}</td>
                  <td>${price.toFixed(2)}</td>
                  <td>
                    {editingPrice === symbol ? (
                      <div className="price-edit">
                        <input
                          type="number"
                          value={newPrice}
                          onChange={(e) => setNewPrice(e.target.value)}
                          placeholder={price.toString()}
                          step="0.01"
                          min="0"
                        />
                        <button onClick={() => handlePriceUpdate(symbol)}>
                          ✓
                        </button>
                        <button onClick={() => setEditingPrice(null)}>✗</button>
                      </div>
                    ) : (
                      <button onClick={() => setEditingPrice(symbol)}>
                        Edit
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Top Performers */}
      {performanceMetrics && (
        <div className="top-performers">
          <div className="best-trade">
            <h4>Best Trade</h4>
            {performanceMetrics.bestTrade ? (
              <div className="trade-summary">
                <div className="trade-symbol">
                  {performanceMetrics.bestTrade.symbol}
                </div>
                <div className="trade-pnl positive">
                  +${performanceMetrics.bestTrade.totalPnL.toFixed(2)}
                </div>
              </div>
            ) : (
              <div className="no-data">No completed trades</div>
            )}
          </div>

          <div className="worst-trade">
            <h4>Worst Trade</h4>
            {performanceMetrics.worstTrade ? (
              <div className="trade-summary">
                <div className="trade-symbol">
                  {performanceMetrics.worstTrade.symbol}
                </div>
                <div className="trade-pnl negative">
                  ${performanceMetrics.worstTrade.totalPnL.toFixed(2)}
                </div>
              </div>
            ) : (
              <div className="no-data">No completed trades</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// Trades Tab Component
const TradesTab: React.FC<{
  tradePnLs: TradePnL[];
  currentPrices: Record<string, number>;
  updateCurrentPrice: (symbol: string, price: number) => void;
}> = ({ tradePnLs, currentPrices, updateCurrentPrice }) => {
  const [sortBy, setSortBy] = useState<keyof TradePnL>("totalPnL");
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">("desc");

  const sortedTrades = [...tradePnLs].sort((a, b) => {
    const aValue = a[sortBy] as number;
    const bValue = b[sortBy] as number;
    return sortOrder === "asc" ? aValue - bValue : bValue - aValue;
  });

  const handleSort = (field: keyof TradePnL) => {
    if (sortBy === field) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortBy(field);
      setSortOrder("desc");
    }
  };

  return (
    <div className="trades-tab">
      <div className="trades-table">
        <table>
          <thead>
            <tr>
              <th onClick={() => handleSort("tradeId")}>
                Trade ID{" "}
                {sortBy === "tradeId" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("symbol")}>
                Symbol{" "}
                {sortBy === "symbol" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("side")}>
                Side {sortBy === "side" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("totalPnL")}>
                Total P&L{" "}
                {sortBy === "totalPnL" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("realizedPnL")}>
                Realized P&L{" "}
                {sortBy === "realizedPnL" && (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("unrealizedPnL")}>
                Unrealized P&L{" "}
                {sortBy === "unrealizedPnL" &&
                  (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th onClick={() => handleSort("returnPercentage")}>
                Return %{" "}
                {sortBy === "returnPercentage" &&
                  (sortOrder === "asc" ? "↑" : "↓")}
              </th>
              <th>Status</th>
            </tr>
          </thead>
          <tbody>
            {sortedTrades.map((trade) => (
              <tr
                key={trade.tradeId}
                className={`trade-row ${trade.totalPnL >= 0 ? "positive" : "negative"}`}
              >
                <td>{trade.tradeId}</td>
                <td>{trade.symbol}</td>
                <td className={`side ${trade.side.toLowerCase()}`}>
                  {trade.side}
                </td>
                <td
                  className={`pnl ${trade.totalPnL >= 0 ? "positive" : "negative"}`}
                >
                  ${trade.totalPnL.toFixed(2)}
                </td>
                <td
                  className={`pnl ${trade.realizedPnL >= 0 ? "positive" : "negative"}`}
                >
                  ${trade.realizedPnL.toFixed(2)}
                </td>
                <td
                  className={`pnl ${trade.unrealizedPnL >= 0 ? "positive" : "negative"}`}
                >
                  ${trade.unrealizedPnL.toFixed(2)}
                </td>
                <td
                  className={`return ${trade.returnPercentage >= 0 ? "positive" : "negative"}`}
                >
                  {trade.returnPercentage.toFixed(2)}%
                </td>
                <td>
                  <span className={`status ${trade.status.toLowerCase()}`}>
                    {trade.status}
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

// Performance Tab Component
const PerformanceTab: React.FC<{
  performanceMetrics: PerformanceMetrics | null;
  dateRange: { start: string; end: string } | null;
  setDateRange: (range: { start: string; end: string } | null) => void;
}> = ({ performanceMetrics, dateRange, setDateRange }) => {
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  const years = Array.from(
    { length: 5 },
    (_, i) => new Date().getFullYear() - i,
  );

  return (
    <div className="performance-tab">
      {/* Date Range Filter */}
      <div className="date-filter">
        <h3>Performance Analysis</h3>
        <div className="filter-controls">
          <select
            value={selectedYear}
            onChange={(e) => setSelectedYear(parseInt(e.target.value))}
          >
            {years.map((year) => (
              <option key={year} value={year}>
                {year}
              </option>
            ))}
          </select>

          <button
            onClick={() =>
              setDateRange({
                start: `${selectedYear}-01-01`,
                end: `${selectedYear}-12-31`,
              })
            }
            className="apply-filter"
          >
            Apply Filter
          </button>

          <button onClick={() => setDateRange(null)} className="clear-filter">
            Clear Filter
          </button>
        </div>
      </div>

      {/* Monthly Performance */}
      {performanceMetrics?.monthlyReturns && (
        <div className="monthly-performance">
          <h3>Monthly Performance ({selectedYear})</h3>
          <div className="monthly-chart">
            {performanceMetrics.monthlyReturns
              .filter((month) =>
                month.month.startsWith(selectedYear.toString()),
              )
              .map((month) => (
                <div key={month.month} className="month-bar">
                  <div className="month-label">{month.month.slice(-2)}</div>
                  <div className="month-pnl">
                    <div
                      className={`pnl-bar ${month.totalPnL >= 0 ? "positive" : "negative"}`}
                      style={{ height: `${Math.abs(month.totalPnL) / 100}px` }}
                    ></div>
                  </div>
                  <div className="month-value">
                    ${month.totalPnL.toFixed(0)}
                  </div>
                </div>
              ))}
          </div>
        </div>
      )}

      {/* Symbol Performance */}
      {performanceMetrics?.symbolBreakdown && (
        <div className="symbol-performance">
          <h3>Performance by Symbol</h3>
          <div className="symbol-table">
            <table>
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>Trades</th>
                  <th>Win Rate</th>
                  <th>Total P&L</th>
                  <th>Average P&L</th>
                  <th>Best Trade</th>
                  <th>Worst Trade</th>
                </tr>
              </thead>
              <tbody>
                {performanceMetrics.symbolBreakdown.map((symbol) => (
                  <tr key={symbol.symbol}>
                    <td>{symbol.symbol}</td>
                    <td>{symbol.tradeCount}</td>
                    <td>{symbol.winRate.toFixed(1)}%</td>
                    <td
                      className={`pnl ${symbol.totalPnL >= 0 ? "positive" : "negative"}`}
                    >
                      ${symbol.totalPnL.toFixed(2)}
                    </td>
                    <td
                      className={`pnl ${symbol.averagePnL >= 0 ? "positive" : "negative"}`}
                    >
                      ${symbol.averagePnL.toFixed(2)}
                    </td>
                    <td className="positive">${symbol.bestTrade.toFixed(2)}</td>
                    <td className="negative">
                      ${symbol.worstTrade.toFixed(2)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

// Charts Tab Component
const ChartsTab: React.FC<{
  performanceMetrics: PerformanceMetrics | null;
  tradePnLs: TradePnL[];
}> = ({ performanceMetrics, tradePnLs }) => {
  return (
    <div className="charts-tab">
      <h3>Performance Charts</h3>

      {/* P&L Distribution Chart */}
      <div className="chart-section">
        <h4>P&L Distribution</h4>
        <div className="pnl-distribution">
          {tradePnLs.length > 0 ? (
            <div className="distribution-chart">
              {/* This would be replaced with a real charting library like Chart.js or Recharts */}
              <div className="chart-placeholder">
                P&L Distribution Chart
                <br />
                <small>Chart visualization would go here</small>
              </div>
            </div>
          ) : (
            <div className="no-data">No trade data available for charts</div>
          )}
        </div>
      </div>

      {/* Win/Loss Ratio Chart */}
      {performanceMetrics && (
        <div className="chart-section">
          <h4>Win/Loss Ratio</h4>
          <div className="win-loss-chart">
            <div className="chart-placeholder">
              Win/Loss Ratio Chart
              <br />
              <small>Chart visualization would go here</small>
            </div>
          </div>
        </div>
      )}

      {/* Monthly Returns Chart */}
      {performanceMetrics?.monthlyReturns && (
        <div className="chart-section">
          <h4>Monthly Returns</h4>
          <div className="monthly-chart">
            <div className="chart-placeholder">
              Monthly Returns Chart
              <br />
              <small>Chart visualization would go here</small>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PnLDashboard;
